import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#placeActions();
    this.#desktopMediaQuery.addEventListener('change', this.#placeActions, { signal });
  }

  #controller = new AbortController();
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // The section response does not include app-injected wishlist markup.
    // Preserve its nodes and listeners only when staying on the same product.
    if (this.dataset.productId && this.dataset.productId === newMediaGallery.dataset.productId) {
      const currentActions = this.querySelector('.product-media__actions--primary');
      const newActions = newMediaGallery.querySelector('.product-media__actions--primary');
      const wishlistSelector = '.swym-button-content, #swym-custom-pdp-button';

      if (currentActions && newActions) {
        for (const child of Array.from(currentActions.children)) {
          if (child.matches(wishlistSelector) || child.querySelector(wishlistSelector)) {
            newActions.append(child);
          }
        }
      }
    }

    this.replaceWith(newMediaGallery);
  };

  #placeActions = () => {
    const actions = this.querySelector('.product-media__actions--primary');
    if (!actions) return;

    const shouldUseGridPlacement = this.presentation === 'grid' && this.#desktopMediaQuery.matches;
    const target = shouldUseGridPlacement
      ? this.querySelector('.media-gallery__grid .product-media-container')
      : this.querySelector('slideshow-container');

    if (target && actions.parentElement !== target) {
      target.append(actions);
    }
  };

  /**
   * Opens the shared Shop the Look drawer from any qualifying gallery image.
   */
  showShopTheLook() {
    const dialogComponent = this.querySelector('.product-media__shop-the-look-dialog dialog-component');
    dialogComponent?.showDialog();
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    const clickPath = event?.composedPath?.() ?? [];
    const isActionsClick = clickPath.some(
      (node) =>
        node instanceof Element &&
        (node.matches(
          '.product-media__actions, .product-media__shop-the-look-action, .swym-button-content, #swym-custom-pdp-button'
        ) ||
          node.closest(
            '.product-media__actions, .product-media__shop-the-look-action, .swym-button-content, #swym-custom-pdp-button'
          ))
    );

    if (isActionsClick) {
      return;
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
